/**
 * Env-gated console helpers for Cursor / Chrome DevTools agents.
 * Installed as window.careAgent only on allowed hosts (never production-like).
 * Distinct from window.careTest (Playwright RT reads; always installed).
 */
import { appSettings } from './appSettings.js';
import EventBus from '../Application/event-bus.js';
import cachingService from '@/Services/cachingService.jsx';

const ALLOWED_ENVIRONMENTS = new Set(['Development', 'Local', 'Staging']);

/** portal-dev / portal-dev1 as hostname or first DNS label (not a bare "-dev" substring). */
const PORTAL_DEV_HOST = /^portal-dev\d*(\.|$)/;
/** CallCorp lab k8s-style hosts, e.g. gp1-dev1-app07 */
const LAB_K8S_HOST = /^gp\d+-dev\d+(-[a-z0-9]+)+(\.|$)/;

/**
 * Hostname / Environment allowlist for exposing agent mutators.
 * Prefer hostname — appSettings.Environment is often "" (prod default) even in lab.
 * Do not use a bare "-dev" substring match (too easy to hit production-adjacent names).
 */
export function isCareAgentHostAllowed() {
    if (typeof window === 'undefined' || !window.location)
        return false;

    const host = String(window.location.hostname || '').toLowerCase();
    if (!host)
        return false;

    if (host === 'localhost' || host === '127.0.0.1')
        return true;
    if (host.endsWith('.localhost'))
        return true;
    if (host === 'callcorplab.com' || host.endsWith('.callcorplab.com'))
        return true;
    if (PORTAL_DEV_HOST.test(host))
        return true;
    if (LAB_K8S_HOST.test(host))
        return true;

    if (ALLOWED_ENVIRONMENTS.has(appSettings.Environment))
        return true;

    return false;
}

function getDebugSettings() {
    return {
        DEBUG: !!appSettings.DEBUG,
        ShowAllLogMessages: !!appSettings.ShowAllLogMessages,
    };
}

/**
 * Ensure Debug Features + Show all log messages are on (same storage as UserMenu/Debug).
 * Does not reload by default — DEBUG icons need a remount; pass reload: true when safe.
 *
 * @param {{ reload?: boolean }} [options]
 * @returns {{ before: object, after: object, changed: boolean, needsReload: boolean }}
 */
function ensureDebugSettings({ reload = false } = {}) {
    const before = getDebugSettings();

    if (!before.DEBUG)
        appSettings.DEBUG = true;
    if (!before.ShowAllLogMessages)
        appSettings.ShowAllLogMessages = true;

    const after = getDebugSettings();
    const changed = before.DEBUG !== after.DEBUG || before.ShowAllLogMessages !== after.ShowAllLogMessages;
    const needsReload = !before.DEBUG && after.DEBUG;

    if (reload && needsReload && typeof location !== 'undefined')
        location.reload();

    return { before, after, changed, needsReload };
}

function normalizeDocName(name) {
    return String(name ?? '').trim().replace(/^\/+/, '');
}

function tabIdForPreview(docName) {
    return `_preview_agent_${docName.replace(/\//g, '_')}`;
}

/**
 * Emit a Global UIAction and wait for FinishFunc.
 * @param {string} actionType
 * @param {object} action
 * @returns {Promise<unknown>}
 */
function emitGlobalAction(actionType, action) {
    return new Promise((resolve, reject) => {
        const msg = `Action-${actionType}`;
        if (!EventBus.$has(msg)) {
            reject(new Error(
                `${msg} has no listeners — open Call Center Home (logged-in portal shell) first`
            ));
            return;
        }

        action.ActionType = actionType;
        action.FinishFunc = function (success, result) {
            if (success)
                resolve(result);
            else
                reject(result ?? new Error(`${actionType} failed`));
        };

        EventBus.$emit(msg, action);
    });
}

function flushDocumentCaches() {
    // Same effect as Preview in Tab / Run menu FlushControlCache + FlushUrlCache.
    cachingService.clearCache('APICache');
}

/**
 * Open a UserControl in a portal tab — same path as Authoring → User Controls → Preview in Tab.
 * Requires Call Center Home (Action-Tab listener).
 *
 * @param {string} name Document Name (e.g. "general/forms/resetpassword" or "public/v1/Vue/...")
 * @returns {Promise<{ ok: true, name: string, kind: 'UserControl', tabID: string }>}
 */
async function openUserControl(name) {
    const docName = normalizeDocName(name);
    if (!docName)
        throw new Error('openUserControl: name is required');

    flushDocumentCaches();

    const tabID = tabIdForPreview(docName);

    await emitGlobalAction('Tab', {
        ActionData: {
            ControlData: {
                BreadCrumb: { Action: 'Replace' },
                DisplayFeedback: 'Default',
                Controls: [
                    {
                        PrimitiveControl: false,
                        ControlData: {},
                        ControlURL: docName,
                        Name: 'Unnamed',
                        CacheControl: true,
                    },
                ],
                Vars: { Name: docName },
                SizeOptions: {},
                Header: {},
                FloatingContainerConfig: {},
            },
            TabTitle: docName,
            TabID: tabID,
        },
        // onActionTab evaluates these; plain strings pass through utils.evaluate unchanged
        tabID,
        tabTitle: docName,
        context: {},
    });

    return { ok: true, name: docName, kind: 'UserControl', tabID };
}

/**
 * Run a User Action by name — same path as Authoring → User Actions → Run (ReferenceUserAction).
 * Requires ActionService (normal logged-in portal).
 *
 * @param {string} name Action Name / ActionURL
 * @returns {Promise<{ ok: true, name: string, kind: 'Action' }>}
 */
async function runUserAction(name) {
    const docName = normalizeDocName(name);
    if (!docName)
        throw new Error('runUserAction: name is required');

    flushDocumentCaches();

    await emitGlobalAction('ReferenceUserAction', {
        ActionData: {
            UserAction: {
                ActionData: {},
                ActionURL: docName,
                CacheAction: false,
            },
        },
        actionURL: docName,
        context: { Input: {} },
    });

    return { ok: true, name: docName, kind: 'Action' };
}

/**
 * @param {string} name
 * @param {{ type?: 'UserControl' | 'Action' }} [options] Default UserControl
 */
async function open(name, { type = 'UserControl' } = {}) {
    if (type === 'Action')
        return runUserAction(name);
    return openUserControl(name);
}

function requireCallCenterHome() {
    const home = appSettings.careCallCenterHome;
    if (!home) {
        throw new Error(
            'Call Center Home is not registered — open the logged-in portal shell first'
        );
    }
    return home;
}

/**
 * Plain JSON rail + submenu tree (title, name, path, navigable, children).
 * Prefers live DynamicMenuList; else builds MenuName children from document_cache.
 */
function getNavigationTree() {
    const home = requireCallCenterHome();
    if (typeof home.getAgentNavigationTree !== 'function')
        throw new Error('getAgentNavigationTree unavailable on Call Center Home');
    return home.getAgentNavigationTree();
}

/**
 * Navigate to a menu path in one call (rail OnActivate + leaf SelectItem / OnActivate).
 * Path: '/' -joined Titles as shown in UI (Name accepted per segment).
 *
 * @param {string} path e.g. "Authoring/User Controls"
 * @returns {Promise<{ ok: boolean, path: string, matched?: string[], error?: string }>}
 */
async function navigateMenu(path) {
    const home = requireCallCenterHome();
    if (typeof home.selectMenuByPath !== 'function')
        throw new Error('selectMenuByPath unavailable on Call Center Home');
    return home.selectMenuByPath(path);
}

export function installCareAgentProbe() {
    if (typeof window === 'undefined')
        return;

    if (!isCareAgentHostAllowed())
        return;

    window.careAgent = {
        getDebugSettings,
        ensureDebugSettings,
        openUserControl,
        runUserAction,
        open,
        getNavigationTree,
        navigateMenu,
    };
}

export default {
    installCareAgentProbe,
    isCareAgentHostAllowed,
    getDebugSettings,
    ensureDebugSettings,
    openUserControl,
    runUserAction,
    open,
    getNavigationTree,
    navigateMenu,
};
